import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { 
    Send, Paperclip, ArrowLeft, MoreVertical, Check, 
    CheckCheck, Search, X, Maximize2, MessageSquare 
} from 'lucide-react';

const SOCKET_URL = 'http://localhost:2026';
const socket = io(SOCKET_URL);

const BuyerChatHistoryPage: React.FC = () => {
    const navigate = useNavigate();
    const { conversationId } = useParams();
    const [searchParams] = useSearchParams();
    
    const [conversations, setConversations] = useState<any[]>([]); 
    const [messages, setMessages] = useState<any[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const paramName = searchParams.get('companyName') || 'Seller';
    const paramProduct = searchParams.get('productName') || 'Enquiry';

    // Sidebar Logic: Teal theme updates & scrollable list
    useEffect(() => {
        const fetchList = async () => {
            try {
                const res = await fetch(`${SOCKET_URL}/api/chat/buyer-conversations/${user.id}`);
                const data = await res.json();
                setConversations(Array.isArray(data) ? data : []);
            } catch (err) { console.error("Error fetching chat list", err); }
        };
        if (user.id) fetchList();

        // Listen for new messages to update badges in sidebar
        socket.on('receive_message', (data) => {
            fetchList(); 
        });

        return () => { socket.off('receive_message'); };
    }, [user.id]);

    useEffect(() => {
        if (user.id) socket.emit('join_room', { userId: user.id, userType: 'buyer' });

        if (conversationId) {
            fetch(`${SOCKET_URL}/api/chat/messages/${conversationId}`)
                .then(res => res.json())
                .then(data => setMessages(Array.isArray(data) ? data : []));
        }
    }, [conversationId, user.id]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const sendMessage = (e?: any, attachmentUrl?: string, attachmentType?: string) => {
        if (e) e.preventDefault();
        if (!newMessage.trim() && !attachmentUrl) return;

        const currentChat = conversations.find(c => c.conversationId == conversationId);
        const messageData = {
            conversationId, senderId: user.id, senderType: 'buyer',
            receiverId: currentChat?.sellerId, messageText: newMessage,
            attachmentUrl: attachmentUrl || null, attachmentType: attachmentType || null
        };

        socket.emit('send_message', messageData);
        setMessages((prev) => [...prev, { ...messageData, is_read: 0, created_at: new Date().toISOString() }]);
        setNewMessage('');
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', e.target.files[0]);
            try {
                const res = await fetch(`${SOCKET_URL}/api/chat/upload`, { method: 'POST', body: formData });
                const data = await res.json();
                if (data.fileUrl) sendMessage(null, data.fileUrl, data.fileType);
            } catch (err) { alert("Upload failed"); } 
            finally { setUploading(false); }
        }
    };

    return (
        <div className="flex h-[85vh] bg-[#f0f2f5] overflow-hidden">
            
            {previewImage && (
                <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md">
                    <button onClick={() => setPreviewImage(null)} className="absolute top-5 right-5 text-white"><X size={32} /></button>
                    <img src={previewImage} className="max-w-full max-h-full object-contain" alt="preview" />
                </div>
            )}

            {/* --- SIDEBAR: Teal Green Theme & Auto-Scroll --- */}
            <div className={`w-full md:w-[400px] bg-white border-r flex flex-col ${conversationId ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 bg-[#008080] flex items-center justify-between shadow-md">
                    <div className="flex items-center gap-3">
                        <ArrowLeft className="cursor-pointer text-white" onClick={() => navigate('/buyer/profile?section=profile')} />
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#008080] font-bold uppercase shadow-sm">
                            {user.username?.charAt(0)}
                        </div>
                        <h2 className="font-bold text-white tracking-wide text-lg">My Chats</h2>
                    </div>
                </div>

                <div className="p-3 bg-white border-b">
                    <div className="flex items-center gap-3 bg-[#f0f2f5] px-4 py-2 rounded-full border border-gray-100 focus-within:ring-2 focus-within:ring-[#008080] transition-all">
                        <Search size={16} className="text-gray-400" />
                        <input type="text" placeholder="Search chats..." className="bg-transparent border-none outline-none text-sm w-full" />
                    </div>
                </div>

                {/* SCROLLABLE LIST */}
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50">
                    {conversations.map((chat) => (
                        <div 
                            key={chat.conversationId} 
                            onClick={() => navigate(`/buyer/chats/${chat.conversationId}?companyName=${chat.companyName}&productName=${chat.productName}`)}
                            className={`flex items-center gap-3 p-4 border-b cursor-pointer hover:bg-teal-50 transition-all relative ${conversationId == chat.conversationId ? 'bg-teal-50 border-l-4 border-l-[#008080]' : 'bg-white'}`}
                        >
                            <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center text-teal-800 font-bold shadow-inner uppercase text-xl">
                                {chat.companyName?.charAt(0)}
                            </div>
                            <div className="flex-1 overflow-hidden">
                                <div className="flex justify-between items-center mb-0.5">
                                    <h4 className="font-bold text-sm text-gray-800 truncate">{chat.companyName}</h4>
                                    <span className="text-[10px] text-gray-400">12:30 PM</span>
                                </div>
                                <p className="text-[11px] text-[#008080] font-black truncate uppercase tracking-tighter">For: {chat.productName}</p>
                                <p className="text-xs text-gray-500 truncate mt-0.5">{chat.lastMessage || 'Sent a media'}</p>
                            </div>

                            {/* --- WHATSAPP STYLE NOTIFICATION BADGE --- */}
                            {chat.unreadCount > 0 && (
                                <div className="flex flex-col items-end gap-1">
                                    <div className="bg-[#25D366] text-white text-[10px] font-bold h-5 min-w-5 flex items-center justify-center rounded-full px-1 shadow-sm">
                                        {chat.unreadCount}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* --- CHAT AREA --- */}
            <div className={`flex-1 flex flex-col bg-[#efeae2] relative ${!conversationId ? 'hidden md:flex' : 'flex'}`}>
                {conversationId ? (
                    <>
                        <div className="p-3 bg-[#f0f2f5] border-b flex items-center justify-between sticky top-0 z-50 shadow-sm">
                            <div className="flex items-center gap-3">
                                <ArrowLeft className="md:hidden cursor-pointer text-gray-600" onClick={() => navigate('/buyer/chats')} />
                                <div className="w-10 h-10 bg-[#008080] rounded-full flex items-center justify-center text-white font-bold uppercase shadow-md">
                                    {paramName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 text-sm leading-tight">{paramName}</h3>
                                    <p className="text-[10px] text-[#008080] font-black truncate max-w-[200px] uppercase">Enquiry: {paramProduct}</p>
                                </div>
                            </div>
                            <MoreVertical size={20} className="text-gray-400 cursor-pointer" />
                        </div>

                        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 md:px-12 space-y-2 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat pb-24">
                            {messages.map((msg, idx) => {
                                const isMe = (msg.sender_id === user.id) || (msg.senderId === user.id);
                                return (
                                    <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                        <div className={`relative max-w-[85%] md:max-w-[70%] px-3 py-1.5 rounded-lg shadow-sm text-sm ${
                                            isMe ? 'bg-[#d9fdd3] text-gray-900 rounded-tr-none' : 'bg-white text-gray-800 rounded-tl-none'
                                        }`}>
                                            {(msg.attachment_url || msg.attachmentUrl) && (
                                                <img src={msg.attachment_url || msg.attachmentUrl} className="rounded mb-1 max-w-full cursor-pointer border" onClick={() => setPreviewImage(msg.attachment_url || msg.attachmentUrl)} />
                                            )}
                                            <p className="inline-block pb-3 pr-8 leading-snug whitespace-pre-wrap">{msg.message_text || msg.messageText}</p>
                                            <div className="absolute bottom-1 right-2 flex items-center gap-1">
                                                <span className="text-[9px] text-gray-400">10:45 AM</span>
                                                {isMe && (msg.is_read ? <CheckCheck size={14} className="text-[#53bdeb]" /> : <Check size={14} className="text-gray-400" />)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="p-3 bg-gray-200 border-t flex items-center gap-3 sticky bottom-0 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                            <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*" />
                            <Paperclip className="text-gray-500 cursor-pointer hover:text-[#008080]" onClick={() => fileInputRef.current?.click()} size={24} />
                            <div className="flex-1 bg-white rounded-full px-5 py-2.5 shadow-sm border border-gray-200 focus-within:border-[#008080] transition-all">
                                <input 
                                    type="text" 
                                    value={newMessage} 
                                    onChange={(e) => setNewMessage(e.target.value)} 
                                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()} 
                                    placeholder="Type a message" 
                                    className="w-full bg-transparent border-none outline-none text-sm" 
                                />
                            </div>
                            <button onClick={() => sendMessage()} disabled={uploading} className="bg-[#008080] text-white p-3 rounded-full shadow-lg hover:bg-[#006666] transition active:scale-95 disabled:bg-gray-400">
                                <Send size={20} />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center bg-[#f8f9fa] text-center p-10">
                        <div className="bg-teal-50 p-10 rounded-full mb-6">
                            <MessageSquare size={80} className="text-[#008080] opacity-40" />
                        </div>
                        <h2 className="text-3xl font-light text-gray-600 mb-2">TradyFrog Web</h2>
                        <p className="text-sm text-gray-400 max-w-sm">Select a conversation from the left to start chatting with suppliers.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BuyerChatHistoryPage;